<template>
    <v-container fluid class="pa-3" v-if="sessionActive">

        <material-header title="Registration statistics"
                         help="help-registration-reports"
                         info="Understand registration efficiency and conversion rates"
                         icon="mdi-account-clock">

            <template slot="buttons">

                <!-- REFRESH -->
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn :disabled="!hasPermission(minPermission)"
                               @click="refreshClicked()"
                               v-on="on"
                               icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>Reload data</span>
                </v-tooltip>
            </template>

        </material-header>

        <v-alert v-if="!hasPermission(minPermission)"
                 class="body-2"
                 type="error"
        >
            Your user account doesn't have sufficient permissions to use this feature
        </v-alert>

        <v-row v-if="hasPermission(minPermission)">

            <v-col cols="12" class="pt-0">

                <v-card flat :loading="statsLoading">
                    <v-card-text>

                        <v-container fluid class="pt-0">

                            <v-row dense>
                                <v-col cols="12" class="text-right">
                                    <v-select
                                        v-model="selectedSubPanelModel"
                                        :items="subPanels"
                                        item-text="name"
                                        item-value="id"
                                        solo
                                        color="white"
                                        background-color="grey darken-2"
                                        hide-details
                                        return-object
                                        @input="subPanelChanged"
                                    ></v-select>
                                </v-col>
                            </v-row>

                            <v-row dense v-if="registrationSurveyExists">

                                <v-col cols="12"  lg="2" md="3" sm="6" xs="12">
                                    <v-slide-x-transition>
                                        <stat-box-numerical v-if="stats && !statsLoading"
                                                            hint="New panelists"
                                                            color="green darken-2"
                                                            :value="stats.total_confirmed">
                                        </stat-box-numerical>
                                    </v-slide-x-transition>
                                </v-col>

                                <v-col cols="12"  lg="2" md="3" sm="6" xs="12">
                                    <v-slide-x-transition>
                                        <stat-box-numerical v-if="stats && !statsLoading"
                                                            hint="Conversion rate"
                                                            postfix="%"
                                                            :color="conversionBoxColor"
                                                            :value="conversionRate">
                                        </stat-box-numerical>
                                    </v-slide-x-transition>
                                </v-col>

                                <v-col cols="12" lg="2" md="3" sm="6" xs="12">
                                    <v-slide-x-transition>
                                        <stat-box-numerical v-if="stats && !statsLoading"
                                                            hint="Surveys started"
                                                            :value="stats.total_started">
                                        </stat-box-numerical>
                                    </v-slide-x-transition>
                                </v-col>

                                <v-col cols="12" lg="2" md="3" sm="6" xs="12">
                                    <v-slide-x-transition>
                                        <stat-box-numerical v-if="stats && !statsLoading"
                                                            hint="Surveys completed"
                                                            :value="stats.total_completes">
                                        </stat-box-numerical>
                                    </v-slide-x-transition>
                                </v-col>

                                <v-col cols="12" lg="2" md="3" sm="6" xs="12">
                                    <v-slide-x-transition>
                                        <stat-box-numerical v-if="stats && !statsLoading"
                                                            hint="# of emails opened"
                                                            :value=stats.open_count
                                                            mouse-over-hint="Emails delivered"
                                                            :mouse-over-value="stats.delivery_count"
                                        ></stat-box-numerical>
                                    </v-slide-x-transition>
                                </v-col>

                                <v-col cols="12" lg="2" md="3" sm="6" xs="12">
                                    <v-slide-x-transition>
                                        <stat-box-numerical v-if="stats && !statsLoading"
                                                            hint="Emails bounced"
                                                            :color="bounceBoxColor"
                                                            :value="stats.bounce_count">
                                        </stat-box-numerical>
                                    </v-slide-x-transition>
                                </v-col>

                            </v-row>

                        </v-container>

                        <!-- LOADER -->
                        <div v-if="statsLoading">
                            <chart-loader :loading="statsLoading"
                                          :no-data="noData"
                                          height="350"
                                          width="100%"
                            ></chart-loader>
                        </div>

                        <!-- SURVEY DOES NOT EXIST -->
                        <v-card flat v-if="!statsLoading && !registrationSurveyExists">
                            <v-card-text>
                                <v-alert type="info" prominent class="body-2">
                                    Registration survey has not been configured
                                </v-alert>
                            </v-card-text>
                        </v-card>

                        <!-- CHART DIV -->
                        <v-scale-transition>
                            <div class="chart pt-4" ref="chartdiv"></div>
                        </v-scale-transition>

                        <!-- REGISTRATION LINKS -->
                        <v-slide-x-transition>
                            <v-card-text class="pb-0" v-if="registrationSurveyExists">
                                <registration-links v-if="!statsLoading && selectedSubPanelModel"
                                                    class="mt-2"
                                                    :sub-panel="selectedSubPanelModel"
                                ></registration-links>
                            </v-card-text>
                        </v-slide-x-transition>

                        <json v-if="debug" :data="selectedSubPanelModel"></json>

                    </v-card-text>
                </v-card>


            </v-col>

        </v-row>
    </v-container>
</template>

<script>

import Json from 'vue-json-pretty';
import Store from '../store';
import {mapState, mapActions} from 'vuex';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import ChartLoader from "../../../components/UI/ChartLoader";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import themeDark from "@amcharts/amcharts4/themes/dark";
import StatBoxNumerical from "../../../components/UI/StatBoxNumerical";
import RegistrationLinks from "../../SubPanels/components/RegistrationLinks";

am4core.useTheme(themeDark);
am4core.useTheme(am4themes_animated);

const vuexModule = 'RegistrationStats';

export default {

    mixins: [activeSessionChecker],

    components: {
        RegistrationLinks,
        StatBoxNumerical,
        Json,
        MaterialHeader,
        ChartLoader
    },

    data() {
        return {
            debug: false,
            minPermission: 'view sub panels',
            noData: false,
            chart: null,
            selectedSubPanelModel: null,
            registrationSurveyExists: true,
        }
    },

    watch: {
        selectedSubPanelModel() {
            this.getRegistrationData();
        }
    },

    computed: {

        ...mapState(vuexModule, [
            'stats',
            'statsLoading',
        ]),

        ...mapState('SubPanels', [
            'subPanels',
        ]),

        conversionRate() {

            if (this.stats) {

                if (this.stats.total_confirmed === 0 || this.stats.total_started === 0) {
                    return 0;
                }

                return Math.round(
                    this.stats.total_confirmed / this.stats.total_started * 100
                )
            }
            return 0;
        },

        conversionBoxColor() {

            if (this.conversionRate < 20) {
                return "red";
            }
            if (this.conversionRate < 30) {
                return "yellow darken-4";
            }

            if (this.conversionRate < 40) {
                return "yellow darken-3";
            }

            return "green darken-2"
        },

        bounceBoxColor() {

            if (this.stats) {

                if (this.stats.bounce_count === 0 || this.stats.total_completed) return 'primary'

                if (this.stats.bounce_count / this.stats.total_completes * 100 > 5) {
                    return "red darken-2";
                }
            }

            return 'primary';
        },

        chartData() {
            if (this.stats) {

                return this.stats.records.map(record => {

                    // Remove overlap for stacked bar chart
                    const startedCount = record.started_count - record.completed_count;
                    const completedCount = record.completed_count - record.confirmed_count;

                    return {
                        started_count: startedCount,
                        completed_count: completedCount,
                        confirmed_count: record.confirmed_count,
                        period: record.period
                    };
                });
            }
        }
    },

    methods: {

        ...mapActions('SubPanels', [
            'loadSubPanels'
        ]),

        ...mapActions(vuexModule, [
            'loadRegistrationStats'
        ]),

        subPanelChanged() {
            this.getRegistrationData()
        },

        refreshClicked() {
            this.registrationSurveyExists = true;
            this.getRegistrationData();
        },

        getRegistrationData() {

            this.loadRegistrationStats(this.selectedSubPanelModel.id)
                .then(response => {
                    this.createChart();
                })
                .catch(error => {

                    if (error.response.status === 404) {
                        this.registrationSurveyExists = false;
                    } else {
                        Bus.$emit('flash-message', {type: 'error', message: 'Registration statistics failed to load'});
                    }
                })
        },

        createChart() {

            let chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);

            chart.paddingRight = 20;
            chart.paddingLeft = 0;
            chart.paddingBottom = 0;
            chart.paddingTop = 40;

            chart.cursor = new am4charts.XYCursor();
            chart.dateFormatter.inputDateFormat = "yyyy-MM-dd HH:mm:ss";
            chart.exporting.menu = new am4core.ExportMenu();
            chart.exporting.menu.align = "right";
            chart.exporting.menu.verticalAlign = "top";

            chart.colors.list = [
                am4core.color("#777777"),
                am4core.color("#ddb347"),
                am4core.color("#388E3C")
            ];

            let dateAxis = chart.xAxes.push(new am4charts.DateAxis());

            // Moved column labels in the center of each column
            dateAxis.renderer.grid.template.location = 0;
            // dateAxis.renderer.cellStartLocation = 0.2;
            // dateAxis.renderer.cellEndLocation = 0.8;
            //dateAxis.renderer.labels.template.rotation = 90;

            dateAxis.dateFormatter = new am4core.DateFormatter();
            //categoryAxis.dateFormatter.dateFormat = "MM-dd";
            //dateAxis.dateFormats.setKey("day", "MMM dt");
            dateAxis.periodChangeDateFormats.setKey("hour", "MMM dt");
            //dateAxis.periodChangeDateFormats.setKey("hour", "HH:mm");
            dateAxis.baseInterval = {
                timeUnit: "hour",
                count: 1
            };
            dateAxis.groupData = true;
            dateAxis.groupCount = 60 * 2;
            dateAxis.renderer.minWidth = 100;

            let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
            valueAxis.renderer.minWidth = 100;
            valueAxis.tooltip.disabled = true;
            valueAxis.min = 0;

            let seriesStarted = chart.series.push(new am4charts.ColumnSeries());
            seriesStarted.showOnInit = false;
            seriesStarted.name = "Abandoned surveys";
            seriesStarted.dataFields.valueY = "started_count";
            seriesStarted.dataFields.dateX = "period";
            seriesStarted.columns.template.fillOpacity = 0.7;
            seriesStarted.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesStarted.stacked = true;

            let seriesCompleted = chart.series.push(new am4charts.ColumnSeries());
            seriesCompleted.showOnInit = false;
            seriesCompleted.name = "Pending email confirmation";
            seriesCompleted.dataFields.valueY = "completed_count";
            seriesCompleted.dataFields.dateX = "period";
            seriesCompleted.columns.template.fillOpacity = 0.7;
            seriesCompleted.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesCompleted.stacked = true;

            let seriesConfirmed = chart.series.push(new am4charts.ColumnSeries());
            seriesConfirmed.name = "New panelists";
            seriesConfirmed.showOnInit = false;
            seriesConfirmed.dataFields.valueY = "confirmed_count";
            seriesConfirmed.dataFields.dateX = "period";
            seriesConfirmed.columns.template.fillOpacity = 0.7;
            seriesConfirmed.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesConfirmed.stacked = true;

            chart.data = this.chartData;

            this.chart = chart;
        },
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }

        this.loadSubPanels()
            .then(() => {
                this.selectedSubPanelModel = this.subPanels[0];
            });
    }
}

</script>

<style scopred>
.chart {
  min-height: 350px;
}
</style>
